/**
 * components/SubscriptionBanner.jsx
 *
 * Exports:
 *   default SubscriptionBanner  — trial/expired states
 *   ReferralBanner              — always-visible refer & earn strip
 */

import React, { useState } from 'react';
import { apiFetch } from '../services/api';

// ─── ReferralBanner ───────────────────────────────────────────────────────────
export function ReferralBanner({ user }) {
  const [copied, setCopied]           = useState(false);
  const [referralCode, setReferralCode] = useState(user?.referral_code || null);

  // Fetch from DB if not already on the user object
  React.useEffect(() => {
    if (referralCode) return;
    apiFetch('/api/users/me/referral')
      .then(data => { if (data?.referral_code) setReferralCode(data.referral_code); })
      .catch(() => {});
  }, []);

  const referralLink = referralCode
    ? `https://www.raji.ai/signup?ref=${referralCode}`
    : null;

  const marketingMessage = referralLink
    ? `I found this exciting AI CEO service at Raji.ai that you will like — use the link below to register and we both get free tasks from their highly talented AI employees!\n\n${referralLink}`
    : null;

  function handleCopy() {
    if (!marketingMessage) return;
    navigator.clipboard.writeText(marketingMessage).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    });
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      gap: '12px', flexWrap: 'wrap', padding: '12px 16px', marginBottom: '20px',
      borderRadius: 'var(--r)',
      background: 'rgba(93,202,165,0.06)',
      border: '1px solid rgba(93,202,165,0.25)',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <span style={{ fontSize: '18px' }}>🎁</span>
        <div>
          <span style={{ fontSize: '13px', fontWeight: '600', color: 'var(--ink)' }}>
            Refer a friend, get 2,000 points.
          </span>
          <span style={{ fontSize: '13px', color: 'var(--ink-soft)', marginLeft: '6px' }}>
            They get 2,000 points too.
          </span>
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
        <div style={{
          padding: '5px 12px', borderRadius: 'var(--r)',
          background: 'var(--white)', border: '1px solid var(--paper-3)',
          fontSize: '12px', color: 'var(--ink-mute)', fontFamily: 'monospace',
          maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        }}>
          {referralLink || 'Loading…'}
        </div>
        <button onClick={handleCopy} disabled={!referralLink} style={{
          padding: '5px 14px', fontSize: '12px', fontWeight: '500',
          background: copied ? 'rgba(93,202,165,0.15)' : 'var(--white)',
          color: copied ? '#2d8a6e' : 'var(--ink)',
          border: `1px solid ${copied ? 'rgba(93,202,165,0.4)' : 'var(--paper-3)'}`,
          borderRadius: 'var(--r)', cursor: referralLink ? 'pointer' : 'not-allowed',
          fontFamily: 'var(--sans)', transition: 'all 0.15s', whiteSpace: 'nowrap',
          opacity: referralLink ? 1 : 0.5,
        }}>
          {copied ? '✓ Copied!' : 'Copy invite message'}
        </button>
      </div>
    </div>
  );
}

// ─── SubscriptionBanner ───────────────────────────────────────────────────────
export default function SubscriptionBanner({ user }) {
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');

  if (!user) return null;
  const status   = user.subscription_status || 'trial_active';
  const daysLeft = user.trial_days_remaining ?? 3;
  if (status === 'active') return null;

  async function handleSubscribe() {
    setLoading(true);
    setError('');
    try {
      const { url } = await apiFetch('/api/billing/create-checkout-session', { method: 'POST' });
      window.location.href = url;
    } catch {
      setError('Could not start checkout. Please try again.');
      setLoading(false);
    }
  }

  if (status === 'trial_active') {
    const urgency = daysLeft <= 1;
    return (
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        gap: '12px', flexWrap: 'wrap', padding: '12px 16px', marginBottom: '20px',
        borderRadius: 'var(--r)',
        background: urgency ? 'rgba(200,86,58,0.06)' : 'rgba(14,14,13,0.03)',
        border: `1px solid ${urgency ? 'rgba(200,86,58,0.2)' : 'var(--paper-3)'}`,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div style={{ display: 'flex', gap: '4px' }}>
            {[1, 2, 3].map(d => (
              <div key={d} style={{
                width: '8px', height: '8px', borderRadius: '50%',
                background: d <= (3 - daysLeft)
                  ? (urgency ? 'var(--accent)' : 'var(--ink)') : 'var(--paper-3)',
              }} />
            ))}
          </div>
          <div style={{ fontSize: '13px', color: 'var(--ink)', lineHeight: 1.5 }}>
            <strong>Your AI CEO is ready — build starts tonight.</strong>
            {' '}Multi-skill AI employees at just $1.60/day. They work while you sleep. Free for 3 days and then $49 per month.
          </div>
        </div>
        <button onClick={handleSubscribe} disabled={loading} style={{
          padding: '7px 16px', fontSize: '12px', fontWeight: '600',
          background: urgency ? 'var(--accent)' : 'var(--ink)',
          color: '#fff', border: 'none', borderRadius: 'var(--r)',
          cursor: loading ? 'not-allowed' : 'pointer',
          fontFamily: 'var(--sans)', opacity: loading ? 0.7 : 1, whiteSpace: 'nowrap',
        }}>
          {loading ? 'Loading…' : 'Hire AI Employees — Start Free'}
        </button>
      </div>
    );
  }

  // trial_expired
  return (
    <div style={{ marginBottom: '24px', borderRadius: 'var(--r)', overflow: 'hidden', border: '1px solid var(--paper-3)' }}>
      <div style={{ height: '3px', background: 'linear-gradient(90deg, var(--accent), #e8a87c)' }} />
      <div style={{ padding: '28px 28px 24px', background: 'var(--white)' }}>
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', fontSize: '11px', fontWeight: '600', letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent)', marginBottom: '10px' }}>
            <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--accent)' }} />
            Your free trial has ended
          </div>
          <h2 style={{ fontFamily: 'var(--serif)', fontSize: '26px', fontWeight: '400', color: 'var(--ink)', lineHeight: 1.25, marginBottom: '8px' }}>
            Hire your AI employees.
          </h2>
          <p style={{ fontSize: '14px', color: 'var(--ink-soft)', lineHeight: 1.6, maxWidth: '480px' }}>
            Your agents research, write, build and strategise — every single night —
            while you focus on what only you can do.
          </p>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '12px', marginBottom: '24px' }} className="sub-value-grid">
          {[
            { icon: '🌙', title: 'Works overnight',      desc: 'Agents run every night so you wake up to results' },
            { icon: '🧠', title: 'Thinks strategically', desc: 'CEO agent plans sprints, delegates to specialists' },
            { icon: '📈', title: 'Compounds over time',  desc: 'More context each run = smarter output every day' },
          ].map(({ icon, title, desc }) => (
            <div key={title} style={{ padding: '14px 16px', borderRadius: 'var(--r)', background: 'var(--paper)', border: '1px solid var(--paper-3)' }}>
              <div style={{ fontSize: '20px', marginBottom: '8px' }}>{icon}</div>
              <div style={{ fontSize: '13px', fontWeight: '600', color: 'var(--ink)', marginBottom: '3px' }}>{title}</div>
              <div style={{ fontSize: '12px', color: 'var(--ink-mute)', lineHeight: 1.5 }}>{desc}</div>
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
              <span style={{ fontFamily: 'var(--serif)', fontSize: '36px', fontWeight: '400', color: 'var(--ink)' }}>$49</span>
              <span style={{ fontSize: '14px', color: 'var(--ink-mute)' }}>/month</span>
              <span style={{ fontSize: '12px', color: 'var(--ink-mute)', padding: '2px 8px', borderRadius: '100px', background: 'var(--paper)', border: '1px solid var(--paper-3)', marginLeft: '4px' }}>$1.60 / day</span>
            </div>
            <p style={{ fontSize: '12px', color: 'var(--ink-mute)', marginTop: '2px' }}>
              Cancel anytime · Unlimited workspaces · No setup fees
            </p>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            <button onClick={handleSubscribe} disabled={loading} style={{
              padding: '12px 28px', fontSize: '14px', fontWeight: '600',
              background: 'var(--ink)', color: 'var(--paper)', border: 'none',
              borderRadius: 'var(--r)', cursor: loading ? 'not-allowed' : 'pointer',
              fontFamily: 'var(--sans)', opacity: loading ? 0.7 : 1,
              transition: 'background 0.15s', whiteSpace: 'nowrap',
            }}
              onMouseEnter={e => { if (!loading) e.currentTarget.style.background = 'var(--accent)'; }}
              onMouseLeave={e => { e.currentTarget.style.background = 'var(--ink)'; }}
            >
              {loading ? 'Redirecting to checkout…' : 'Start now · $49/mo →'}
            </button>
            {error && <span style={{ fontSize: '12px', color: 'var(--accent)' }}>{error}</span>}
          </div>
        </div>
      </div>
      <style>{`@media (max-width: 640px) { .sub-value-grid { grid-template-columns: 1fr !important; } }`}</style>
    </div>
  );
}